import debounce from 'debounce';

import CharacterUtils from '../utils/CharacterUtils';
import { SessionNames, EventCategories, EventActions, EventLabels } from './constants';
import { arrayToLabel, logEvent } from './utils';

const logCharacterCampaignClicked = () => {
    logEvent(SessionNames.DDB, {
        category: EventCategories.DDB_Character,
        action: EventActions.DDB_Character_Campaign,
        label: EventLabels.Clicked,
    });
};

const logCharacterCopyCancelled = () => {
    logEvent(SessionNames.DDB, {
        category: EventCategories.DDB_Character,
        action: EventActions.DDB_Character_Copy,
        label: EventLabels.Cancelled,
    });
};

const logCharacterCopyClicked = () => {
    logEvent(SessionNames.DDB, {
        category: EventCategories.DDB_Character,
        action: EventActions.DDB_Character_Copy,
        label: EventLabels.Clicked,
    });
};

const logCharacterCopyConfirmed = () => {
    logEvent(SessionNames.DDB, {
        category: EventCategories.DDB_Character,
        action: EventActions.DDB_Character_Copy,
        label: EventLabels.Confirmed,
    });
};

const logCharacterDeleteCancelled = () => {
    logEvent(SessionNames.DDB, {
        category: EventCategories.DDB_Character,
        action: EventActions.DDB_Character_Delete,
        label: EventLabels.Cancelled,
    });
};

const logCharacterDeleteClicked = () => {
    logEvent(SessionNames.DDB, {
        category: EventCategories.DDB_Character,
        action: EventActions.DDB_Character_Delete,
        label: EventLabels.Clicked,
    });
};

const logCharacterDeleteConfirmed = () => {
    logEvent(SessionNames.DDB, {
        category: EventCategories.DDB_Character,
        action: EventActions.DDB_Character_Delete,
        label: EventLabels.Confirmed,
    });
};

const logCharacterEditClicked = () => {
    logEvent(SessionNames.DDB, {
        category: EventCategories.DDB_Character,
        action: EventActions.DDB_Character_Edit,
        label: EventLabels.Clicked,
    });
};

const logCharacterLeaveCampaignCancelled = () => {
    logEvent(SessionNames.DDB, {
        category: EventCategories.DDB_Character,
        action: EventActions.DDB_Character_LeaveCampaign,
        label: EventLabels.Cancelled,
    });
};

const logCharacterLeaveCampaignClicked = () => {
    logEvent(SessionNames.DDB, {
        category: EventCategories.DDB_Character,
        action: EventActions.DDB_Character_LeaveCampaign,
        label: EventLabels.Clicked,
    });
};

const logCharacterLeaveCampaignConfirmed = () => {
    logEvent(SessionNames.DDB, {
        category: EventCategories.DDB_Character,
        action: EventActions.DDB_Character_LeaveCampaign,
        label: EventLabels.Confirmed,
    });
};

const logCharacterViewClicked = () => {
    logEvent(SessionNames.DDB, {
        category: EventCategories.DDB_Character,
        action: EventActions.DDB_Character_View,
        label: EventLabels.Clicked,
    });
};

const logListingSearchChanged = debounce((characters, matchesSearch) => {
    const matchedLabels = characters
        .map(
            (character) =>
                CharacterUtils.getSearchableTermsAnalyticsLabel(character)[
                    CharacterUtils.getSearchableTerms(character).findIndex(matchesSearch)
                ],
        )
        .filter(Boolean)
        .filter((character, index, characters) => characters.indexOf(character) === index);

    logEvent(SessionNames.DDB, {
        category: EventCategories.DDB_ListingFilter,
        action: EventActions.DDB_ListingFilter_SearchChanged,
        label: arrayToLabel(matchedLabels) || 'no match',
    });
}, 500);

const logListingSearchCleared = () => {
    logEvent(SessionNames.DDB, {
        category: EventCategories.DDB_ListingFilter,
        action: EventActions.DDB_ListingFilter_SearchCleared,
    });
};

const logListingSortChanged = (label) => {
    logEvent(SessionNames.DDB, {
        category: EventCategories.DDB_ListingSort,
        action: EventActions.DDB_ListingSort_Changed,
        label,
    });
};

const logPlayerAppBannerDismissed = () => {
    logEvent(SessionNames.DDB, {
        category: EventCategories.DDB_PlayerAppBanner,
        action: EventActions.DDB_PlayerAppBanner_Dismissed,
    });
};

const logPlayerAppBannerClickedCta = (label) => {
    logEvent(SessionNames.DDB, {
        category: EventCategories.DDB_PlayerAppBanner,
        action: EventActions.DDB_PlayerAppBanner_ClickedCta,
        label,
    });
};

const logUnlockCharacterLocked = () => {
    logEvent(SessionNames.DDB, {
        category: EventCategories.DDB_Unlock,
        action: EventActions.DDB_Unlock_CharacterLocked,
    });
};

const logUnlockCharacterUnlocked = () => {
    logEvent(SessionNames.DDB, {
        category: EventCategories.DDB_Unlock,
        action: EventActions.DDB_Unlock_CharacterUnlocked,
    });
};

const logUnlockFinishUnlockingClicked = (unlockedCharacterCount, maxSlots) => {
    logEvent(SessionNames.DDB, {
        category: EventCategories.DDB_Unlock,
        action: EventActions.DDB_Unlock_FinishUnlockingClicked,
        label: `Unlocked Characters: ${unlockedCharacterCount} / ${maxSlots}`,
    });
};

const logUnlockFinishUnlockingCancelled = (unlockedCharacterCount, maxSlots) => {
    logEvent(SessionNames.DDB, {
        category: EventCategories.DDB_Unlock,
        action: EventActions.DDB_Unlock_FinishUnlockingCancelled,
        label: `Unlocked Characters: ${unlockedCharacterCount} / ${maxSlots}`,
    });
};

const logUnlockFinishUnlockingConfirmed = (unlockedCharacterCount, maxSlots) => {
    logEvent(SessionNames.DDB, {
        category: EventCategories.DDB_Unlock,
        action: EventActions.DDB_Unlock_FinishUnlockingConfirmed,
        label: `Unlocked Characters: ${unlockedCharacterCount} / ${maxSlots}`,
    });
};

const logUnlockSubscribeClicked = () => {
    logEvent(SessionNames.DDB, {
        category: EventCategories.DDB_Unlock,
        action: EventActions.DDB_Unlock_SubscribeClicked,
    });
};

export default {
    logCharacterCampaignClicked,
    logCharacterCopyCancelled,
    logCharacterCopyClicked,
    logCharacterCopyConfirmed,
    logCharacterDeleteCancelled,
    logCharacterDeleteClicked,
    logCharacterDeleteConfirmed,
    logCharacterEditClicked,
    logCharacterLeaveCampaignCancelled,
    logCharacterLeaveCampaignClicked,
    logCharacterLeaveCampaignConfirmed,
    logCharacterViewClicked,

    logListingSearchChanged,
    logListingSearchCleared,
    logListingSortChanged,

    logPlayerAppBannerDismissed,
    logPlayerAppBannerClickedCta,

    logUnlockCharacterLocked,
    logUnlockCharacterUnlocked,
    logUnlockFinishUnlockingClicked,
    logUnlockFinishUnlockingCancelled,
    logUnlockFinishUnlockingConfirmed,
    logUnlockSubscribeClicked,
};
